import { InfoCircleOutlined } from "@ant-design/icons";
import { Radio, Tooltip, Typography } from "antd";
import type { RadioChangeEvent } from "antd/es/radio";
import { StyleSheet } from "../../../StyleSheet";
import type { ServicioMegaMovil } from "../../../types/common";

type LineType = "SIM" | "ESIM";

interface LineTypeSelectorProps {
    packageType?: string;
    lineTypeSelections: LineType[];
    lineDefinitions?: ServicioMegaMovil[];
    onLineTypeChange: (lineIndex: number, lineType: LineType) => void;
}

const { Text } = Typography;

const LineTypeSelector = (props: LineTypeSelectorProps) => {
    const {
        packageType,
        lineTypeSelections,
        lineDefinitions = [],
        onLineTypeChange,
    } = props;

    const handleSegmentChange = (lineIndex: number, event: RadioChangeEvent) => {
        const normalizedValue: LineType = event.target.value === "ESIM" ? "ESIM" : "SIM";
        onLineTypeChange(lineIndex, normalizedValue);
    };

    return (
        <div style={styles.linesContainer}>
            <div style={styles.linesHeaderRow}>
                <Text style={styles.linesHeaderText}>
                    Líneas incluidas: {lineTypeSelections.length}
                </Text>
                <Tooltip
                    title={"Es una SIM digital integrada en tu celular que te permite activar tu línea sin usar una tarjeta física (solo para equipos compatibles)."}
                >
                    <InfoCircleOutlined style={styles.tooltipIcon} />
                </Tooltip>
            </div>
            <div style={styles.line} />
            {lineTypeSelections.map((selectedLineType, index) => {
                const isFamilyPackage = packageType === "FAMILIAR";
                const backendLineType = lineDefinitions[index]?.tipo_linea?.toUpperCase();
                const additionalLineNumber = lineDefinitions
                    .slice(0, index + 1)
                    .filter((line) => line.tipo_linea?.toUpperCase() === "A").length;

                let lineTitle = `Línea ${index + 1}`;
                if (isFamilyPackage) {
                    if (backendLineType === "P") {
                        lineTitle = "Línea principal";
                    } else if (backendLineType === "A") {
                        lineTitle = `Línea adicional ${additionalLineNumber}`;
                    } else {
                        lineTitle = index === 0 ? "Línea principal" : `Línea adicional ${index}`;
                    }
                }

                const lineSubtitle = selectedLineType === "ESIM"
                    ? "Activación digital"
                    : "Chip físico requerido";

                return (
                    <div key={`line-type-${index}`}>
                        <div style={styles.lineTypeRow}>
                            <div style={styles.lineTypeTextContainer}>
                                <Text style={styles.lineTypeTitle}>{lineTitle}</Text>
                                <Text style={styles.lineTypeSubtitle}>{lineSubtitle}</Text>
                            </div>
                            <Radio.Group
                                value={selectedLineType}
                                onChange={(event) => handleSegmentChange(index, event)}
                                style={styles.segmentedControl}
                            >
                                <Radio.Button
                                    value="SIM"
                                    style={{
                                        ...styles.segmentButton,
                                        ...styles.segmentButtonLeft,
                                        ...(selectedLineType === "SIM" ? styles.segmentButtonActive : {}),
                                    }}
                                >
                                    <span style={{
                                        ...styles.segmentButtonText,
                                        ...(selectedLineType === "SIM" ? styles.segmentButtonTextActive : {}),
                                    }}>
                                        SIM
                                    </span>
                                </Radio.Button>
                                <Radio.Button
                                    value="ESIM"
                                    style={{
                                        ...styles.segmentButton,
                                        ...(selectedLineType === "ESIM" ? styles.segmentButtonActive : {}),
                                    }}
                                >
                                    <span style={{
                                        ...styles.segmentButtonText,
                                        ...(selectedLineType === "ESIM" ? styles.segmentButtonTextActive : {}),
                                    }}>
                                        eSIM
                                    </span>
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                        {index < lineTypeSelections.length - 1 && (
                            <div style={styles.line} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const styles = StyleSheet.create({
    linesContainer: {
        width: "100%",
        border: "1px solid #d6dde8",
        borderRadius: 12,
        backgroundColor: "#ffffff",
        padding: "8px 12px",
        marginTop: 10,
        marginBottom: 10,
    },
    line: {
        borderBottom: "0.8px solid #d6dde8",
        width: "100%",
        margin: "2px 0",
    },
    linesHeaderRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 30,
        padding: "2px 0",
    },
    linesHeaderText: {
        fontSize: 14,
        color: "#202428",
        fontWeight: 600,
    },
    tooltipIcon: {
        color: "#0055c2",
        fontSize: 16,
        cursor: "pointer",
    },
    lineTypeRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
        padding: "6px 0",
    },
    lineTypeTextContainer: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        paddingRight: 8,
        gap: 2,
    },
    lineTypeTitle: {
        fontSize: 12,
        color: "#202428",
        fontWeight: 600,
    },
    lineTypeSubtitle: {
        fontSize: 11,
        color: "#6f7480",
        whiteSpace: "nowrap",
        lineHeight: "13px",
    },
    segmentedControl: {
        display: "inline-flex",
        border: "1px solid #0055c2",
        borderRadius: 20,
        overflow: "hidden",
        backgroundColor: "#ffffff",
        boxShadow: "none",
        flexShrink: 0,
        // marginTop: 2
    },
    segmentButton: {
        minWidth: 45,
        height: 28,
        border: "none",
        borderRadius: 0,
        backgroundColor: "#ffffff",
        color: "#0055c2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 6px",
    },
    segmentButtonLeft: {
        borderRight: "1px solid #0055c2",
    },
    segmentButtonActive: {
        backgroundColor: "#0055c2",
    },
    segmentButtonText: {
        fontSize: 12,
        fontWeight: 600,
        color: "#0055c2",
        lineHeight: "12px",
    },
    segmentButtonTextActive: {
        color: "#ffffff",
    },
});

export default LineTypeSelector;
